import { useState } from "react";
import { useAppRule, useApps } from "../api/hooks.js";
import { StateBadge } from "../components/StateBadge.js";
import { Empty, PageState } from "../components/PageState.js";
export function GameLibraryPage({ deviceId }: { deviceId: string }) {
  const apps = useApps(deviceId);
  const rule = useAppRule(deviceId);
  const [filter, setFilter] = useState("");
  const [state, setState] = useState("all");
  if (!deviceId) return <Empty label="Select a headset to manage its games." />;
  const rows = (apps.data ?? [])
    .filter(
      (a) =>
        (a.package_id + " " + (a.display_name ?? ""))
          .toLowerCase()
          .includes(filter.toLowerCase()) &&
        (state === "all" || a.allow_state === state),
    )
    .sort((a, b) =>
      (a.display_name ?? a.package_id).localeCompare(
        b.display_name ?? b.package_id,
      ),
    );
  return (
    <PageState query={apps}>
      <div className="search-bar">
        <input
          type="search"
          aria-label="Search games"
          placeholder="Find a game or app…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <select
          aria-label="Filter game rules"
          value={state}
          onChange={(e) => setState(e.target.value)}
        >
          <option value="all">All games</option>
          <option value="allowed">Allowed</option>
          <option value="denied">Blocked</option>
          <option value="unrated">Not reviewed</option>
          <option value="quarantined">Quarantined</option>
        </select>
      </div>
      <p className="muted">
        {rows.length} apps · Rules apply across detected headset profiles.
      </p>
      {rule.error && (
        <p className="notice danger" role="alert">
          Couldn’t save the rule. {rule.error.message}
        </p>
      )}
      {rule.isSuccess && (
        <p className="notice" role="status">
          Rule saved. The headset will apply it on its next sync.
        </p>
      )}
      {!rows.length ? (
        <Empty label="No games match. Try a different search or filter." />
      ) : (
        <div className="game-grid">
          {rows.map((a) => {
            const name =
              a.display_name || a.package_id.split(".").at(-1) || a.package_id;
            return (
              <article className="card game-card" key={a.package_id}>
                <div className="game-avatar" aria-hidden="true">
                  {name.slice(0, 1).toUpperCase()}
                </div>
                <div className="game-body">
                  <h3>{name}</h3>
                  <StateBadge state={a.allow_state} />
                  <select
                    aria-label={`Set state for ${a.package_id}`}
                    value={a.allow_state}
                    disabled={rule.isPending}
                    onChange={(e) =>
                      rule.mutate({
                        packageId: a.package_id,
                        allowState: e.target.value,
                      })
                    }
                  >
                    <option value="allowed">Allow this game</option>
                    <option value="denied">Block this game</option>
                    {!["allowed", "denied"].includes(a.allow_state) && (
                      <option value={a.allow_state}>
                        {a.allow_state === "unrated"
                          ? "Not reviewed"
                          : "Quarantined"}
                      </option>
                    )}
                  </select>
                  <details>
                    <summary className="muted">App details</summary>
                    <p className="mono">{a.package_id}</p>
                    <p className="muted">Version {a.version ?? "unknown"}</p>
                  </details>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </PageState>
  );
}
